import { SxProps, Theme } from '@mui/material';
import colors from '../../common/colors';

export const SECTION_CLASS = 'SectionedDataGrid-section';
export const BAND_CLASS = 'SectionedDataGrid-band';
export const GRID_WRAPPER_CLASS = 'SectionedDataGrid-gridWrapper';
export const STICKY_DETAIL_PANEL_CLASS = 'SectionedDataGrid-stickyDetailPanel';

/**
 * Cells tagged with this class don't toggle their row's detail panel when
 * clicked, so a cell with its own tap interaction (e.g. a tooltip) keeps that
 * behaviour instead of expanding the row. Apply it via a column's `cellClassName`.
 */
export const ROW_CLICK_IGNORE_CLASS = 'SectionedDataGrid-rowClickIgnore';

/**
 * Class applied to rows that carry a divider rule. A row gets it either by
 * returning it from `getRowClassName`, or automatically when `getRowDividerColor`
 * resolves a color for the row. Consumers supply the actual border styling
 * through `gridSx` (reading `DIVIDER_COLOR_VAR` for the color), keeping this
 * component style-agnostic.
 */
export const DIVIDER_ROW_CLASS = 'SectionedDataGrid-dividerRow';

/**
 * CSS custom property carrying a row's divider-rule color. `SectionedDataGrid`
 * sets it per row from `getRowDividerColor`; consumers read it in `gridSx`, e.g.
 * `borderBottom: 2px solid var(${DIVIDER_COLOR_VAR})`.
 */
export const DIVIDER_COLOR_VAR = '--SectionedDataGrid-dividerColor';

type StylesConfig = {
  container: (borderRadius: string) => SxProps<Theme>;
  band: SxProps<Theme>;
  gridWrapper: SxProps<Theme>;
  stickyDetailPanel: SxProps<Theme>;
};

const styles: StylesConfig = {
  container: (borderRadius: string) => ({
    backgroundColor: colors.white,
    border: `1px solid ${colors.neutral_300}`,
    borderRadius,
    // Sections stack flush — the coloured bands are the only separators
    display: 'flex',
    flexDirection: 'column',
    // `min-width: 0` lets this flex item shrink below its content so the grid's
    // own scroller absorbs the overflow instead of pushing the page wide.
    minWidth: 0,
    // Only the first band and the last grid get rounded corners; the
    // sections in between stay square so the stack reads as one panel
    // (the grid root carries a 4px default radius that must be flattened)
    '& .MuiDataGrid-root': {
      borderRadius: 0,
    },
    [`& .${SECTION_CLASS}:first-of-type .${BAND_CLASS}`]: {
      borderTopLeftRadius: borderRadius,
      borderTopRightRadius: borderRadius,
    },
    [`& .${SECTION_CLASS}:last-of-type .MuiDataGrid-root`]: {
      borderBottomLeftRadius: borderRadius,
      borderBottomRightRadius: borderRadius,
    },
    // The no-rows/loading overlay needs room when a section has no rows —
    // inert otherwise (the wrapper only exists while an overlay is shown)
    '& .MuiDataGrid-overlayWrapper, & .MuiDataGrid-overlayWrapperInner': {
      minHeight: '104px',
    },
  }),
  band: {
    color: colors.white,
    fontWeight: 600,
    fontSize: '16px',
    padding: '8px 24px',
  },
  gridWrapper: {
    // Flex parent with no max height — the grid expands to show all rows
    // (no internal scrolling), so the page itself scrolls
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: 0,
    // Detail panels must not be scroll containers (MUI defaults them to
    // `overflow: auto`): the sticky panel wrapper has to pin against the
    // grid's own horizontal scroller, not against the panel.
    '& .MuiDataGrid-detailPanel': {
      overflow: 'visible',
    },
    // Breathing room above a row that an expansion scrolls to the top of the
    // viewport, so it never sits flush against the edge.
    '& .MuiDataGrid-row': {
      scrollMarginTop: '8px',
    },
  },
  // Pins the panel to the viewport's left edge; width is set inline by StickyDetailPanel.
  stickyDetailPanel: {
    position: 'sticky',
    left: 0,
    boxSizing: 'border-box',
  },
};

export default styles;
